import { Component, ElementRef, OnInit, signal, ViewChild, effect, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { MemberSearchFilterComponent } from '../../../member/components/member-search-filter/member-search-filter.component';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { PhonePipe } from '../../../../core/pipes/phone-number/phone-number.pipe';
import { RecipientSelectorBaseClass, RecipientSelectionState } from '../../../../core/base-classes/recipient-selector-base-class';
import { MemberModel } from '../../../../core/models/data/member.model';
import { MembersFilter } from '../../../../core/filters/members.filter';
import { MemberService } from '../../../../core/services/member.service';
import { MemberUtilities } from '../../../../core/utilities/member.utilities';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { CommunicationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { MemberStatuses, SelectionPrimaryKeys } from '../../../../core/enums/data.enums';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { PaginationConstants } from '../../../../core/constants/constants';
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';

export type MemberSelectionState = RecipientSelectionState<MembersFilter>;

@Component({
  selector: 'ms-communications-member-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    SortDropdownComponent,
    MemberSearchFilterComponent,
    NoValuePipe,
    PhonePipe,
    PaginationComponent,
    InViewPortDirective,
    MobileCardCheckbox,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    TooltipDirective
  ],
  templateUrl: './communications-member-selector.component.html',
  styleUrl: './communications-member-selector.component.scss'
})
export class CommunicationsMemberSelectorComponent extends RecipientSelectorBaseClass<MembersFilter> implements OnInit {

  //#region Properties & Signals

  public organizationId = input.required<number>();

  public selectionChanged = output<MemberSelectionState>();

  public memberList = signal<MemberModel[]>([]);
  public isLoadingMembers = false;
  public loadError = signal(false);
  public searchTerm = '';

  // A private filter copy so this selector never mutates the shared userSessionService.membersFilter.
  // Initialized here (not in ngOnInit) so it's ready before the constructor's emit effect first runs.
  public membersFilter: MembersFilter = ListConstants.MEMBERS_EMAIL_BLAST_FILTER_DEFAULT();

  public readonly membersFilterDefaultFactory = ListConstants.MEMBERS_EMAIL_BLAST_FILTER_DEFAULT;
  public skeletonMembers: MemberModel[] = [];

  public SelectionPrimaryKeys = SelectionPrimaryKeys;
  public CommunicationsMessagingConstants = CommunicationsMessagingConstants;

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  //#endregion Properties & Signals

  //#region Constructor & Initialization

  constructor(private memberService: MemberService) {
    super();
    this.sortOptions = ListConstants.MEMBERS_SORT_OPTIONS();
    this.orderBy = ListConstants.MEMBERS_ORDERBY_DEFAULT();
    this.createSkeletonMembers();

    // Re-emit whenever the picked/excluded set or the matching total changes.
    effect(() => {
      this.selectedRecipients();
      this.totalMatching();
      this.emitState();
    });
  }

  private emitState(): void {
    // Include the active search term so select-all resolves exactly the set shown in the list.
    this.selectionChanged.emit(this.buildSelectionState({ ...this.membersFilter, searchTerm: this.searchTerm }));
  }

  ngOnInit(): void {
    this.loadMembers(false);
  }

  private createSkeletonMembers(): void {
    this.skeletonMembers = Array.from({ length: this.take }, (_, index) => ({
      memberId: index,
      organizationId: 0,
      firstName: 'Loading',
      lastName: 'Member',
      emailAddress: 'loading@example.com',
      phoneNumber: '000-000-0000',
      status: MemberStatuses.Active
    } as MemberModel));
  }

  //#endregion Constructor & Initialization

  //#region Data Loading

  public get displayMembers(): MemberModel[] {
    const memberListValue = this.memberList();
    if (this.isLoadingMembers && (!memberListValue || memberListValue.length === 0)) {
      return this.skeletonMembers;
    }
    return memberListValue || [];
  }

  public async loadMembers(append: boolean = false): Promise<void> {
    const orgId = this.organizationId();
    if (!orgId) {
      return;
    }

    this.isLoadingMembers = true;

    if (append === false) {
      this.resetPagination();
    }

    try {
      this.loadError.set(false);
      this.membersFilter.organizationIds = [orgId];

      const result = await this.memberService.searchMembers(
        { ...this.membersFilter, searchTerm: this.searchTerm },
        this.orderBy,
        this.skip(),
        this.take
      );

      if (result.success && result.data) {
        if (append === true) {
          this.memberList.set(this.memberList().concat(result.data.data || []));
        } else {
          // NOTE: selection is intentionally NOT cleared here — picks accumulate across searches/filters.
          this.memberList.set(result.data.data || []);
        }
        this.totalCount = result.data.total || 0;
        this.totalMatching.set(result.data.total || 0);
        this.hasNext = result.data.hasNext || false;
      } else {
        this.memberList.set([]);
        this.loadError.set(true);
      }
    } catch {
      this.memberList.set([]);
      this.loadError.set(true);
    } finally {
      this.isLoadingMembers = false;
      // Cover filter changes where the matching total happens to be unchanged (effect wouldn't re-fire).
      this.emitState();
    }
  }

  public async loadMore(): Promise<void> {
    this.loadMembers(true);
  }

  public onFilterApplied(filter: MembersFilter): void {
    this.membersFilter = { ...filter };
    this.resetExclusionsForNewResultSet();
    this.loadMembers(false);
  }

  //#endregion Data Loading

  //#region Sorting & Pagination

  public onSearch(): void {
    this.resetExclusionsForNewResultSet();
    this.loadMembers(false);
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.resetExclusionsForNewResultSet();
    this.loadMembers(false);
  }

  // Select-all exclusions are scoped to the filter/search they were made under; once the matching set
  // changes those exclusions are meaningless and would wrongly subtract from the new total, so drop them.
  // Pick-mode selections are intentionally preserved so they keep accumulating across searches. Sorting
  // doesn't change the set, so it deliberately does not reset.
  private resetExclusionsForNewResultSet(): void {
    if (this.isSelectAll) {
      this.selectedRecipients.set(new Map());
    }
  }

  public onSortChange(value: string): void {
    this.orderBy = value;
    this.loadMembers(false);
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  //#endregion Sorting & Pagination

  //#region Selection

  public onMemberToggle(memberId: number, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.onToggle({ id: memberId, checked: checkbox.checked });
  }

  public onToggle(data: { id: number; checked: boolean }): void {
    const member = this.memberList().find(m => m.memberId === data.id);
    this.toggleSelection(data.id, member ? MemberUtilities.fullName(member) : `#${data.id}`, data.checked);
  }

  //#endregion Selection

}
